/**
 * Trial-to-paid gating shared by the server (auth/me + verify responses,
 * card-only endpoint guards) and the client (post-verify routing, app-shell
 * redirect, onboarding payment page, inactive-subscription notice).
 *
 * The rule this file encodes: on modular pricing a saved card is NOT a
 * subscription. Elapsed trial time never creates a Stripe subscription — only
 * completing the subscription-mode Stripe Checkout does — so a billable
 * modular owner counts as "set up" only once a live subscription exists.
 * Legacy (pre-cutover, accountKind null) accounts keep the older card-on-file
 * gate because the modular Checkout route refuses them (409 NOT_MODULAR).
 */

/** Stripe statuses that mean a subscription object is still alive. */
export const LIVE_SUBSCRIPTION_STATUSES: readonly string[] = ["active", "trialing", "past_due"];

/** Stripe statuses that mean a previously-live subscription has ended. */
const ENDED_SUBSCRIPTION_STATUSES: readonly string[] = ["canceled", "unpaid", "incomplete_expired"];

export interface SubscriptionGateInput {
  accountKind: string | null | undefined;
  subscriptionStatus: string | null | undefined;
  stripeSubscriptionId: string | null | undefined;
  founderStatus?: string | null;
  /** Card on file (app_users.stripeDefaultPaymentMethodId is set). */
  hasPaymentMethod: boolean;
  /**
   * 'v3' accounts get their Stripe trial at signup with no card; the trial,
   * pause and past-due banners replace the Checkout-first redirect.
   */
  billingModel?: string | null;
}

/** True when the account holds a live (active / trialing / past_due) Stripe subscription. */
export function hasLiveSubscription(
  input: Pick<SubscriptionGateInput, "subscriptionStatus" | "stripeSubscriptionId">,
): boolean {
  return !!input.stripeSubscriptionId && LIVE_SUBSCRIPTION_STATUSES.includes(input.subscriptionStatus ?? "");
}

/** Comped (staff/manual) and active-founder accounts never go through Checkout. */
export function isCheckoutExempt(
  input: Pick<SubscriptionGateInput, "subscriptionStatus" | "founderStatus">,
): boolean {
  return input.subscriptionStatus === "comped" || input.founderStatus === "active";
}

/**
 * Whether a billable owner still has to complete the payment step.
 *   comped / founder → never
 *   modular          → until a live subscription exists (a card alone does not count)
 *   legacy           → until a card is on file (unchanged pre-cutover gate)
 * Callers still apply the owner/team-member/admin/ToS checks themselves.
 */
export function needsPaymentSetup(input: SubscriptionGateInput): boolean {
  if (input.billingModel === "v3") return false;
  if (isCheckoutExempt(input)) return false;
  if (input.accountKind == null) return !input.hasPaymentMethod;
  return !hasLiveSubscription(input);
}

/** `needsPaymentSetup` for a raw app_users row (server-side response builders). */
export function needsPaymentSetupForUser(user: {
  accountKind?: string | null;
  subscriptionStatus?: string | null;
  stripeSubscriptionId?: string | null;
  founderStatus?: string | null;
  stripeDefaultPaymentMethodId?: string | null;
  billingModel?: string | null;
}): boolean {
  return needsPaymentSetup({
    billingModel: user.billingModel,
    accountKind: user.accountKind,
    subscriptionStatus: user.subscriptionStatus,
    stripeSubscriptionId: user.stripeSubscriptionId,
    founderStatus: user.founderStatus,
    hasPaymentMethod: !!user.stripeDefaultPaymentMethodId,
  });
}

export type InactiveSubscriptionReason = "payment_failed" | "trial_ended" | "subscription_ended";

function toTime(value: Date | string | null | undefined): number | null {
  if (value == null) return null;
  const t = (value instanceof Date ? value : new Date(value)).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Why gated features are locked for this account's OWN billing state, or null
 * when its own billing grants access. Mirrors `isBillingActive` in
 * shared/entitlements.ts: past_due is NOT billing-active (only active,
 * trialing, comped, or a still-running local trial are).
 *   past_due                                  → "payment_failed"
 *   modular, trial over, sub canceled/unpaid  → "subscription_ended"
 *   modular, trial over, never subscribed     → "trial_ended"
 *   modular, no trial clock, never subscribed → null (not started yet:
 *                                               Checkout grants the trial)
 * Legacy accounts only resolve past_due here; their other 402s come from
 * tier gates and are described generically by the client.
 */
export function inactiveSubscriptionReason(input: {
  accountKind: string | null | undefined;
  subscriptionStatus: string | null | undefined;
  founderStatus?: string | null;
  trialEndsAt: Date | string | null | undefined;
  now?: Date;
}): InactiveSubscriptionReason | null {
  if (isCheckoutExempt(input)) return null;
  if (input.subscriptionStatus === "past_due") return "payment_failed";
  if (input.accountKind == null) return null;
  if (input.subscriptionStatus === "active" || input.subscriptionStatus === "trialing") return null;
  if (ENDED_SUBSCRIPTION_STATUSES.includes(input.subscriptionStatus ?? "")) {
    const endsAt = toTime(input.trialEndsAt);
    return endsAt != null && endsAt > (input.now ?? new Date()).getTime() ? null : "subscription_ended";
  }
  const ends = toTime(input.trialEndsAt);
  // No local trial clock means the trial never started — Checkout will grant
  // the standard trial, so this is "not set up yet", not "trial ended".
  if (ends == null) return null;
  if (ends > (input.now ?? new Date()).getTime()) return null;
  return "trial_ended";
}

/**
 * Client read of the server-computed `needsPaymentSetup` flag on auth/me and
 * the verify/login responses. Falls back to the legacy card-on-file signal
 * only when the flag is absent (an older cached payload).
 */
export function needsPaymentSetupFromResponse(u: {
  needsPaymentSetup?: boolean | null;
  hasPaymentMethod?: boolean | null;
} | null | undefined): boolean {
  if (!u) return false;
  return typeof u.needsPaymentSetup === "boolean" ? u.needsPaymentSetup : !u.hasPaymentMethod;
}
